import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import SubscriptionPackageCard from "../components/SubscriptionPackageCard";
import { getPackagesByServiceId, Package } from "../API/packageAPI";
import { formatCurrency } from "../help/Utils";
import {
  getApplication,
  getEligibilityAcknowledgement,
  recordEligibilityAcknowledgement,
} from "../API/applicationAPI";
import type { EligibilityAcknowledgementStatus } from "../API/applicationAPI";
import EligibilityAgreement from "../components/eligibility/EligibilityAgreement";
import { ELIGIBILITY_ACKNOWLEDGEMENT_VERSION } from "../data/eligibilityResultContent";
import type { Application } from "../types/application";

const SubscriptionPackage = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [application, setApplication] = useState<Application | null>(null);

  // Client worklist #79. The dashboard card links straight here, skipping the
  // result page where the eligibility agreement is given — so this page asks
  // for it when the application has none. The tier, and so the wording, come
  // from the server's own evaluation, which also covers applicants arriving
  // on a device that never saw the result page.
  const [agreement, setAgreement] =
    useState<EligibilityAcknowledgementStatus | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [recordingAgreement, setRecordingAgreement] = useState(false);

  // Get visaType and applicationId from URL params or location state
  const visaType =
    searchParams.get("visaType") || location.state?.visaType || "N-400";
  const applicationId =
    searchParams.get("applicationId") || location.state?.applicationId;

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPackagesByServiceId(visaType);
        // Ensure data is an array
        setPackages(Array.isArray(data) ? data : []);
      } catch (err: any) {
        console.error("Error fetching packages:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load packages. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [visaType]);

  // Fetch current application if applicationId present
  useEffect(() => {
    const fetchApplication = async () => {
      if (!applicationId) return;
      try {
        const app = await getApplication(parseInt(applicationId));
        setApplication(app);

        // Validate that application is in a valid stage for package selection
        // Allow disqualified apps that chose "Continue to Forms Anyway"
        const validStages = ["qualified", "uscis_fee_decision", "disqualified"];
        if (app.stage && !validStages.includes(app.stage)) {
          setError(
            `Your application is in "${app.stage}" stage and cannot select a package yet. ` +
              `Please complete the eligibility process first.`
          );
        }
      } catch (err: any) {
        // Non-blocking: log only; user can still pick a package
        console.error("Error fetching application:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load application details. You can still select a package."
        );
      }
    };

    fetchApplication();
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) return;
    getEligibilityAcknowledgement(parseInt(applicationId))
      .then(setAgreement)
      // Non-blocking, like the application fetch above. Payment itself
      // refuses without an agreement, so a failed check here cannot let
      // anyone through.
      .catch((err) => console.error("Could not check eligibility agreement:", err));
  }, [applicationId]);

  const needsAgreement = Boolean(
    agreement?.required && !agreement.acknowledged && agreement.tier
  );

  const handleAgree = async () => {
    if (!applicationId || !agreed) return;
    setRecordingAgreement(true);
    try {
      await recordEligibilityAcknowledgement(
        parseInt(applicationId),
        ELIGIBILITY_ACKNOWLEDGEMENT_VERSION
      );
      setAgreement(await getEligibilityAcknowledgement(parseInt(applicationId)));
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          "Could not save your agreement. Please try again."
      );
    } finally {
      setRecordingAgreement(false);
    }
  };

  // Get button class based on index for visual variety
  const getButtonClass = (index: number) => {
    const classes = [
      "bg-[#00B894] hover:bg-[#009a76]",
      "bg-[#80EFD9] hover:bg-[#132445]",
      "bg-[#6C5CE7] hover:bg-[#5849c7]",
    ];
    return classes[index % classes.length];
  };

  if (loading) {
    return (
      <div className="relative login_main body_padding pb-[100px]">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1B365D] mx-auto mb-4"></div>
            <p className="text-[#1B365D] text-lg">Loading packages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative login_main body_padding pb-[100px]">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-[#1B365D] mb-2">
              Error Loading Packages
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-6 py-3 rounded-[50px] hover:bg-[#F29449]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="relative login_main body_padding pb-[100px]">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h2 className="text-2xl font-bold text-[#1B365D] mb-2">
              No Packages Available
            </h2>
            <p className="text-gray-600">
              We're currently updating our service packages. Please check back
              soon!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <h1 className="mb-0 text-center sm:mb-1 text-[48px] tracking-[-1.7px] leading-[60.35px]">
        Select Your Service Filing Package
      </h1>
      <p className="text-center mb-8 text-lg mt-3">
        Choose the support level that's right for your citizenship journey
      </p>
      {needsAgreement && agreement?.tier && (
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <EligibilityAgreement
            tier={agreement.tier}
            checked={agreed}
            onChange={setAgreed}
            heading="Before You Choose a Package"
            disabled={recordingAgreement}
          />
          <button
            type="button"
            onClick={handleAgree}
            disabled={!agreed || recordingAgreement}
            className="mt-5 w-full rounded-md bg-[#1B365D] px-4 py-3 text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {recordingAgreement ? "Saving…" : "Agree & Continue"}
          </button>
        </div>
      )}
      {(() => {
        const activePackages = packages.filter((pkg) => pkg.isActive);
        // With only one active package the lg:grid-cols-2 layout left-
        // aligned the lone card in the first grid cell. Switch to a
        // single-column grid in that case so `mx-auto` actually centers
        // it. Keep the two-column layout when there are multiple
        // packages so nothing regresses on the common path.
        const isSingle = activePackages.length === 1;
        const gridClasses = isSingle
          ? "mt-10 max-w-[420px] mx-auto grid grid-cols-1 gap-4"
          : "mt-10 max-w-[40%] xl:max-w-[50%] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4";
        return (
          <div className={gridClasses}>
            {activePackages.map((pkg, index) => {
              // Check if application is in a valid stage for package selection
              const validStages = [
                "qualified",
                "uscis_fee_decision",
                "disqualified",
              ];
              const canSelectPackage =
                !applicationId ||
                !application?.stage ||
                validStages.includes(application.stage);

              return (
                <SubscriptionPackageCard
                  key={pkg.id}
                  title={pkg.name}
                  desc={pkg.description}
                  price={formatCurrency(pkg.price)}
                  features={pkg.features}
                  btnClass={getButtonClass(index)}
                  packageId={pkg.id}
                  applicationId={
                    applicationId ? parseInt(applicationId) : undefined
                  }
                  package={pkg}
                  currentPackageId={application?.packageId ?? null}
                  disabled={!canSelectPackage || needsAgreement}
                  disabledReason={
                    needsAgreement
                      ? "Agree to the statements above to choose a package"
                      : undefined
                  }
                />
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

export default SubscriptionPackage;
